import React, { useState } from 'react';
import { getImageSrc, handleImageError, formatDisplayText } from '../utils/showcaseUtils';
import { useConstituentItems } from '../hooks/useConstituentItems';
import ConstituentProductModal from './ConstituentProductModal';
import ProductShowcaseModal from './ProductShowcaseModal';

// Helper function to check if product is a set or combo
const isSetOrCombo = (productCode) => {
  if (!productCode) return false;
  // Check for CKWSET or CmbItm in the product code
  return productCode.includes('CKWSET') || productCode.includes('CmbItm');
};

export default function ProductDetailModal({ product, onClose }) {
  const [imageError, setImageError] = useState(false);
  const [selectedConstituent, setSelectedConstituent] = useState(null);
  const [showConstituentModal, setShowConstituentModal] = useState(false);
  const [showShowcaseModal, setShowShowcaseModal] = useState(false);
  const { constituentItems, loading, error } = useConstituentItems(product.code);

  const handleImageLoadError = (e) => {
    setImageError(true);
    handleImageError(e);
  };

  const handleConstituentItemClick = (item) => {
    setSelectedConstituent(item);
    setShowConstituentModal(true);
  };

  const handleConstituentModalClose = () => {
    setShowConstituentModal(false);
    setSelectedConstituent(null);
  };

  const handleShowcase = () => {
    setShowShowcaseModal(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl max-h-[90vh] overflow-y-auto w-full border border-[#19BFB7]/20">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-[#011841]">{product.name || 'Product Details'}</h2>
            <button
              onClick={onClose}
              className="text-[#033EA5] hover:text-[#022b73] text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>

          <div className="p-6">
            {/* Product Image at Top */}
            <div className="mb-6">
              <div className="relative h-80 bg-white rounded-lg overflow-hidden max-w-md mx-auto">
                {!imageError && product.imageUrl ? (
                  <img
                    src={getImageSrc(product.imageUrl)}
                    alt={product.name || 'Product image'}
                    className="w-full h-full object-contain"
                    onError={handleImageLoadError}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <div className="text-6xl mb-2">🛍️</div>
                      <p className="text-sm">No image available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Details - Full Width Column */}
            <div className="space-y-6">
              {/* Showcase Button Section - Only for sets/combos */}
              {isSetOrCombo(product.code) && (
                <div className="text-center">
                  <button
                    onClick={handleShowcase}
                    className="bg-gradient-to-r from-[#033EA5] to-[#19BFB7] hover:from-[#19BFB7] hover:to-[#033EA5] text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg text-lg"
                  >
                    🎬 View Product Showcase
                  </button>
                  <p className="text-sm text-gray-600 mt-2">
                    Experience the full interactive showcase presentation
                  </p>
                </div>
              )}

              {/* USP/Features */}
              {product.USP && (
                <div>
                  <h3 className="text-lg font-bold text-[#011841] mb-3">✨ Key Features</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-[#033EA5] whitespace-pre-line">{product.USP}</p>
                  </div>
                </div>
              )}

              {/* Description */}
              {product.description && (
                <div>
                  <h3 className="text-lg font-bold text-[#011841] mb-3">📝 Description</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
                  </div>
                </div>
              )}

              {/* Categories */}
              {product.Categories && (
                <div>
                  <h3 className="text-lg font-bold text-[#011841] mb-3">🏷️ Categories</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.Categories.split(',').map((cat, index) => {
                      const cleanCat = cat.trim();
                      const match = cleanCat.match(/^[^ ]+ (.+)$/);
                      const displayName = match ? match[1].trim() : cleanCat;
                      return (
                        <span
                          key={index}
                          className="bg-[#19BFB7]/10 text-[#033EA5] px-3 py-1 rounded-full text-sm"
                        >
                          {displayName}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Constituent Items */}
              {loading && (
                <div className="text-center py-4">
                  <div className="text-sm text-gray-600">Loading set items...</div>
                </div>
              )}
              
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    <strong>⚠️ Error:</strong> Could not load set items: {error}
                  </p>
                </div>
              )}
              
              {!loading && !error && constituentItems.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-[#011841] mb-3">📦 Set Includes ({constituentItems.length} products)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {constituentItems.map(item => (
                      <div 
                        key={item.id} 
                        className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all duration-200"
                        onClick={() => handleConstituentItemClick(item)}
                      >
                        <div className="flex items-start space-x-3">
                          {/* Item Image */}
                          <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            {item.imageUrl ? (
                              <img
                                src={getImageSrc(item.imageUrl)}
                                alt={item.name || 'Item image'}
                                className="w-full h-full object-contain"
                                onError={handleImageError}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <div className="text-lg">🛍️</div>
                              </div>
                            )}
                          </div>
                          
                          {/* Item Details */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#033EA5] truncate">
                              {item.name || item.code}
                            </div>
                            <div className="text-xs text-gray-600">{item.code}</div>
                            {item.USP && (
                              <div className="text-xs text-gray-700 mt-1 line-clamp-2">
                                {formatDisplayText(item.USP, 80)}
                              </div>
                            )}
                            {item.BrowsingGroup && (
                              <div className="mt-1">
                                <span className="inline-block bg-[#033EA5] text-white text-xs px-2 py-0.5 rounded-full">
                                  {item.BrowsingGroup}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Showcase Notice */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>🎯 Showcase Mode:</strong> This is a product demonstration. No pricing or ordering functionality is available.
                </p>
              </div>

              {/* Close Button */}
              <div className="flex justify-center pt-4 space-x-3">
                {isSetOrCombo(product.code) && (
                  <button
                    onClick={handleShowcase}
                    className="bg-gradient-to-r from-[#033EA5] to-[#19BFB7] hover:from-[#19BFB7] hover:to-[#033EA5] text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    🎬 View Showcase
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="border-2 border-[#19BFB7]/30 rounded-lg hover:bg-[#E8F4F8] text-[#033EA5] px-8 py-3 font-medium transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Constituent Product Modal */}
      {showConstituentModal && selectedConstituent && (
        <ConstituentProductModal 
          product={selectedConstituent} 
          onClose={handleConstituentModalClose} 
        />
      )}

      {/* Showcase Modal */}
      {showShowcaseModal && (
        <ProductShowcaseModal
          product={product}
          onClose={() => setShowShowcaseModal(false)}
        />
      )}
    </>
  );
}
